"use client";

import React, { FC, RefObject, useCallback, useRef, useState } from "react";
import { ITicketResponse } from "@finbackoffice/clientbff-client";
import dynamic from "next/dynamic";
import { IModalForwardRefPropsV2 } from "contexts";
import { TicketType } from "@finbackoffice/enums";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import Loading from "components/base/loading/Loading";
import Modal from "components/base/modal/Modal";
import Pagination from "components/base/pagination/Pagination";
import { useTickets, ISportBetsFilterState } from "hooks/sportsbook";
import { useDate, useDevice } from "hooks";
import { ModalTypes } from "utils/constants";
import NoData from "components/base/no-data/NoData";
import BetsHistoryTools from "../tools/BetsHistoryTools";
import SportSingleBetView from "./bet-row/SportSingleBetView";
import SportOtherBetView from "./bet-row/SportOtherBetView";
import styles from "./sport-bets-history.module.sass";

const SportBetDetails = dynamic(() => import("./details/SportBetDetails"), { ssr: false });

const SportBetsHistory: FC = () => {
    const { startOfDay, subDays, endOfDay } = useDate();
    const { betsState, setPagination, filter, pagination, setFilter } = useTickets({
        filter: {
            from: subDays(startOfDay(new Date()), 30),
            to: endOfDay(new Date()),
        },
    });
    const [betDetails, setBetDetails] = useState<ITicketResponse | null>(null);
    const detailsModalRef: RefObject<IModalForwardRefPropsV2 | null> = useRef(null);
    const { isMobileDevice } = useDevice();

    const handleDetailsClick = useCallback(
        (val: ITicketResponse) => {
            setBetDetails(val);
            detailsModalRef.current?.open();
        },
        [setBetDetails],
    );

    const handleFilterChange = useCallback(
        (updatedFilter: ISportBetsFilterState) => {
            setFilter(updatedFilter);
            setPagination((state) => ({
                ...state,
                page: 1,
            }));
        },
        [setFilter, setPagination],
    );

    const onPageChangeHandler = (page: number) => {
        setPagination((state) => ({
            ...state,
            page,
        }));
    };

    const onPerPageHandler = (val: string) => {
        setPagination((state) => ({
            ...state,
            items_per_page: parseInt(val),
            page: 1,
        }));
    };

    return (
        <ErrorBoundary>
            <BetsHistoryTools onChange={handleFilterChange} filter={filter} />
            <div className={styles.content} data-testid="sport-bet-history-content">
                {betsState.loading && <Loading testId="loading-spinner" />}
                {!betsState.loading && (
                    <>
                        {betsState.bets?.map((bet) =>
                            bet.type === TicketType.Single ? (
                                <SportSingleBetView key={bet.id} outcome={bet.bets[0]} bet={bet} />
                            ) : (
                                <SportOtherBetView
                                    key={bet.id}
                                    bet={bet}
                                    showDetailsButton
                                    onDetailsClick={handleDetailsClick}
                                />
                            ),
                        )}
                        {betsState.bets && betsState.bets.length === 0 && <NoData />}
                    </>
                )}
            </div>
            {betsState.bets && !!betsState.bets.length && (
                <Pagination
                    items_per_page={pagination.items_per_page}
                    page={pagination.page}
                    total_pages={pagination.total_pages || 0}
                    onPageChange={onPageChangeHandler}
                    onPerPage={onPerPageHandler}
                />
            )}
            <Modal
                ref={detailsModalRef}
                type={ModalTypes.BET_DETAILS}
                priority
                onClose={() => setBetDetails(null)}
                styleClass="betDetailsModal"
                closeButton={!isMobileDevice}
                animateVariant={isMobileDevice ? "rightFullPage" : "opacity"}>
                <SportBetDetails
                    bet={betDetails}
                    onClose={() => {
                        setBetDetails(null);
                        void detailsModalRef.current?.close();
                    }}
                />
            </Modal>
        </ErrorBoundary>
    );
};

export default SportBetsHistory;
